import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, CheckCircle2, Ban, RotateCw, Pencil, ShieldAlert, BellRing, X, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detailMsg = (err, fb) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
};

const STATUS_STYLE = {
  scheduled: "bg-white/5 text-white/60 border-white/15",
  sending: "bg-amber-500/15 text-amber-300 border-amber-500/35",
  sent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/35",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/35",
  confirmed_paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/35",
  suppressed_manual: "bg-blue-500/15 text-blue-300 border-blue-500/35",
  suppressed_dnd: "bg-white/5 text-white/40 border-white/15",
  needs_owner_review: "bg-red-500/15 text-red-300 border-red-500/40",
  cancelled: "bg-white/5 text-white/30 border-white/10 line-through",
  failed: "bg-red-500/15 text-red-300 border-red-500/40",
};
const STATUS_LABEL = {
  scheduled: "Scheduled", sending: "Sending…", sent: "Sent", paid: "Paid (auto-reconciled)",
  confirmed_paid: "Confirmed Paid (owner)", suppressed_manual: "Already Sent Manually",
  suppressed_dnd: "Suppressed (no eligible channel)", needs_owner_review: "Needs Your Review (ambiguous)",
  cancelled: "Cancelled", failed: "Failed — retryable",
};

const inputCls = "bg-black border border-white/15 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#F2B705]/60 w-full";

export default function PaymentDueCycles({ token, onAuthError }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [cycles, setCycles] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ booking_id: "", due_date: "", amount: "", label: "", recurring: false });
  const [saving, setSaving] = useState(false);
  const [openLog, setOpenLog] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    Promise.all([
      axios.get(`${API}/due-cycles`, auth),
      axios.get(`${API}/insurance-exceptions`, auth),
      axios.get(`${API}/bookings`, auth),
    ]).then(([c, ex, b]) => { setCycles(c.data); setExceptions(ex.data); setBookings(b.data); })
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); else toast.error("Could not load reminders."); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activeBookings = useMemo(() => bookings.filter((b) => b.status !== "cancelled"), [bookings]);

  const create = async () => {
    if (!form.booking_id || !form.due_date || !form.amount) return toast.error("Pick a booking, due date and amount.");
    setSaving(true);
    try {
      await axios.post(`${API}/due-cycles`, { ...form, amount: Number(form.amount) }, auth);
      toast.success("Due reminder scheduled.");
      setForm({ booking_id: "", due_date: "", amount: "", label: "", recurring: false });
      load();
    } catch (e) { toast.error(detailMsg(e, "Could not create reminder.")); }
    finally { setSaving(false); }
  };

  const act = async (id, path, body, okMsg) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/due-cycles/${id}/${path}`, body || {}, auth);
      toast.success(okMsg);
      load();
    } catch (e) { toast.error(detailMsg(e, "Action failed.")); }
    finally { setBusyId(null); }
  };

  const cancel = async (id) => {
    setBusyId(id);
    try { await axios.delete(`${API}/due-cycles/${id}`, auth); toast.success("Reminder cancelled."); load(); }
    catch (e) { toast.error(detailMsg(e, "Could not cancel.")); }
    finally { setBusyId(null); }
  };

  const [editing, setEditing] = useState(null);
  const saveEdit = async () => {
    setBusyId(editing.id);
    try {
      await axios.patch(`${API}/due-cycles/${editing.id}`, {
        due_date: editing.due_date, amount: Number(editing.amount), label: editing.label, recurring: editing.recurring,
      }, auth);
      toast.success("Reminder updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(detailMsg(e, "Could not update.")); }
    finally { setBusyId(null); }
  };

  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const visible = cycles.filter((c) => !filter || c.status === filter);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div data-testid="payment-due-cycles" className="space-y-10">
      <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-[#F2B705]/15 text-[#F2B705]"><BellRing size={18} /></div>
          <div className="text-white font-semibold">Owner-confirmed payment due reminders</div>
        </div>
        <p className="text-white/55 text-sm">
          You enter the exact amount and due date from your own records — this is never derived from a booking's stale
          balance. Reminders auto-send once, at 9:00 AM Central on the due date, and are skipped automatically if the
          balance is already paid, the renter opted out, or it's cancelled. No charge, fee, or ledger change ever happens here.
        </p>
      </div>

      {/* Create form */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3"><Plus size={16} /> Schedule a Due Reminder</h4>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-5 gap-3" data-testid="due-cycle-form">
          <select data-testid="due-cycle-booking-select" className={inputCls + " sm:col-span-2"} value={form.booking_id}
            onChange={(e) => setForm((f) => ({ ...f, booking_id: e.target.value }))}>
            <option value="">Select booking…</option>
            {activeBookings.map((b) => <option key={b.id} value={b.id}>{b.name} — {b.vehicle}</option>)}
          </select>
          <input data-testid="due-cycle-date-input" type="date" className={inputCls} value={form.due_date}
            onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
          <input data-testid="due-cycle-amount-input" type="number" step="0.01" min="0.01" placeholder="Amount $" className={inputCls}
            value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          <input data-testid="due-cycle-label-input" placeholder="Label (optional)" className={inputCls}
            value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
          <label className="flex items-center gap-2 text-xs text-white/60 sm:col-span-2 cursor-pointer">
            <input type="checkbox" data-testid="due-cycle-recurring-checkbox" checked={form.recurring}
              onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked }))} className="accent-[#F2B705]" />
            Recurring weekly (auto-create next week's cycle once this one is sent)
          </label>
          <button data-testid="due-cycle-submit-btn" onClick={create} disabled={saving}
            className="sm:col-span-3 bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Schedule Reminder
          </button>
        </div>
      </div>

      {/* Cycle list */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="flex items-center gap-2 font-display font-bold"><History size={16} /> Due Reminders</h4>
          <select data-testid="due-cycle-filter-select" className={inputCls + " w-auto"} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <div className="space-y-2" data-testid="due-cycle-list">
          {visible.length === 0 && <p className="text-white/35 text-sm">No reminders match this filter.</p>}
          {visible.map((c) => (
            <div key={c.id} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3" data-testid={`due-cycle-row-${c.id}`}>
              {editing?.id === c.id ? (
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                  <input type="date" className={inputCls} value={editing.due_date} data-testid={`due-cycle-edit-date-${c.id}`}
                    onChange={(e) => setEditing((s) => ({ ...s, due_date: e.target.value }))} />
                  <input type="number" step="0.01" className={inputCls} value={editing.amount} data-testid={`due-cycle-edit-amount-${c.id}`}
                    onChange={(e) => setEditing((s) => ({ ...s, amount: e.target.value }))} />
                  <input className={inputCls} value={editing.label} placeholder="Label"
                    onChange={(e) => setEditing((s) => ({ ...s, label: e.target.value }))} />
                  <label className="flex items-center gap-1.5 text-xs text-white/60">
                    <input type="checkbox" checked={editing.recurring} onChange={(e) => setEditing((s) => ({ ...s, recurring: e.target.checked }))} className="accent-[#F2B705]" /> Recurring
                  </label>
                  <div className="flex gap-2">
                    <button data-testid={`due-cycle-save-${c.id}`} onClick={saveEdit} className="text-xs bg-[#F2B705] text-black font-bold px-3 py-1.5 rounded">Save</button>
                    <button onClick={() => setEditing(null)} className="text-xs text-white/50 px-2"><X size={14} /></button>
                  </div>
                </div>
              ) : (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">{c.customer}</span>
                    <span className="text-white/40 text-xs">· {c.vehicle}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[c.status] || ""}`} data-testid={`due-cycle-status-${c.id}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    {c.ambiguous && c.status !== "needs_owner_review" && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-red-500/40 text-red-300 flex items-center gap-1"><ShieldAlert size={10} /> overlaps another reminder</span>
                    )}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    Due {c.due_date} · {c.label} · Amount {money(c.amount)}
                    {c.remaining < c.amount && <span className="text-emerald-400"> · {money(c.paid_toward_cycle)} received · {money(c.remaining)} remaining</span>}
                    {c.recurring && <span className="text-[#F2B705]"> · recurring weekly</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!["cancelled", "confirmed_paid", "suppressed_manual"].includes(c.status) && (
                    <>
                      <button data-testid={`due-cycle-confirm-paid-${c.id}`} disabled={busyId === c.id}
                        onClick={() => act(c.id, "confirm-paid", {}, "Marked confirmed paid — reminder stopped.")}
                        title="Confirm paid (does not touch the ledger)"
                        className="text-xs flex items-center gap-1 border border-emerald-400/40 text-emerald-400 px-2.5 py-1.5 rounded hover:bg-emerald-500 hover:text-white transition-colors disabled:opacity-50">
                        <CheckCircle2 size={12} /> Confirm Paid
                      </button>
                      <button data-testid={`due-cycle-mark-manual-${c.id}`} disabled={busyId === c.id}
                        onClick={() => act(c.id, "mark-sent-manually", {}, "Marked already sent manually — won't auto-send.")}
                        title="I already texted/called the renter myself"
                        className="text-xs flex items-center gap-1 border border-blue-400/40 text-blue-300 px-2.5 py-1.5 rounded hover:bg-blue-500 hover:text-white transition-colors disabled:opacity-50">
                        Already Sent
                      </button>
                      <button data-testid={`due-cycle-send-now-${c.id}`} disabled={busyId === c.id}
                        onClick={() => act(c.id, "send-now", {}, "Send attempted — check status below.")}
                        className="text-xs flex items-center gap-1 border border-[#F2B705]/50 text-[#F2B705] px-2.5 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-50">
                        {busyId === c.id ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Send Now
                      </button>
                      <button data-testid={`due-cycle-edit-${c.id}`} onClick={() => setEditing({ id: c.id, due_date: c.due_date, amount: c.amount, label: c.label, recurring: c.recurring })}
                        className="text-xs flex items-center gap-1 border border-white/20 text-white/60 px-2.5 py-1.5 rounded hover:bg-white/10 transition-colors"><Pencil size={12} /></button>
                      <button data-testid={`due-cycle-cancel-${c.id}`} disabled={busyId === c.id} onClick={() => cancel(c.id)}
                        className="text-xs flex items-center gap-1 border border-red-400/40 text-red-400 px-2.5 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"><Ban size={12} /></button>
                    </>
                  )}
                  {c.status === "failed" && (
                    <button data-testid={`due-cycle-retry-${c.id}`} disabled={busyId === c.id}
                      onClick={() => act(c.id, "send-now", {}, "Retry attempted.")}
                      className="text-xs flex items-center gap-1 border border-amber-400/40 text-amber-300 px-2.5 py-1.5 rounded hover:bg-amber-500 hover:text-white transition-colors">
                      <RotateCw size={12} /> Retry
                    </button>
                  )}
                  <button onClick={() => setOpenLog(openLog === c.id ? null : c.id)} data-testid={`due-cycle-log-toggle-${c.id}`}
                    className="text-xs text-white/40 hover:text-white/70 px-2 py-1.5">Log ({(c.send_log || []).length})</button>
                </div>
              </div>
              )}
              {openLog === c.id && (
                <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-1" data-testid={`due-cycle-log-${c.id}`}>
                  {(c.send_log || []).length === 0 && <p className="text-white/30 text-xs">No activity yet.</p>}
                  {(c.send_log || []).map((l, i) => (
                    <p key={i} className="text-white/45 text-xs"><span className="text-white/70 font-semibold">{l.status}</span> — {l.reason} <span className="text-white/25">({l.at})</span></p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Insurance exceptions */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-1 text-amber-400"><ShieldAlert size={16} /> Insurance Exceptions</h4>
        <p className="text-white/40 text-xs mb-3">Active rentals needing your attention. Upload reminders and AI extraction keep running untouched — this is just a clear status view. AI never auto-approves coverage.</p>
        <div className="space-y-1.5" data-testid="insurance-exceptions-list">
          {exceptions.length === 0 && <p className="text-white/35 text-sm flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> No open insurance exceptions.</p>}
          {exceptions.map((e) => (
            <div key={e.booking_id} className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-amber-500/20 rounded px-3 py-2 text-sm flex-wrap" data-testid={`insurance-exception-${e.booking_id}`}>
              <span>
                <span className="text-white font-semibold">{e.customer}</span>
                <span className="text-white/40"> · {e.vehicle}</span>
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300 font-semibold">{e.exception}</span>
              </span>
              <span className="text-white/40 text-xs">
                {e.expiration_date && `exp ${e.expiration_date} · `}
                {e.reminder_count > 0 && `${e.reminder_count} reminder(s) sent`}
                {e.reminders_stopped && " · reminders stopped"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
